import * as React from "react";
import { cn } from "../../lib/cn";

/**
 * Composer.Textarea — auto-resizing input.
 *
 * - Grows from `minHeight` up to `maxHeight` (default 200px) — beyond that
 *   the inner scroll takes over.
 * - `Enter` (no shift, no IME composition) submits the parent `<form>`.
 * - `Shift+Enter` inserts a newline (default browser behaviour).
 * - `Cmd/Ctrl+Enter` also submits (matches the `⌘⏎` hint shown above).
 * - Controlled OR uncontrolled — when `value` is omitted we fall back to
 *   uncontrolled (internal `defaultValue`), but auto-resize still works
 *   because we re-measure on every `input` event.
 *
 * Port note: v1's `QueryInput.tsx` does `if (e.key === "Enter" && !e.shiftKey)
 * { e.preventDefault(); triggerQuery(query); }` inline. We promote that to
 * the textarea here so every Composer consumer gets the behaviour for free.
 */
export interface ComposerTextareaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "rows"> {
  /** Minimum collapsed height in px. Default `44`. */
  minHeight?: number;
  /** Maximum expanded height in px before inner scroll kicks in. Default `200`. */
  maxHeight?: number;
  /** Imperative submit (parent doesn't have to wire `<form onSubmit>`). */
  onSubmit?: () => void;
}

export const ComposerTextarea = React.forwardRef<HTMLTextAreaElement, ComposerTextareaProps>(
  (
    {
      className,
      minHeight = 44,
      maxHeight = 200,
      onSubmit,
      onChange,
      onKeyDown,
      value,
      defaultValue,
      // Jun 8, 2026 — primitive default drops the hardcoded "Andrea".
      // A composer primitive can't know the user's identity; callers
      // wanting personalised copy must pass ``placeholder`` (see
      // ``routes/_authed/route.tsx``). Deprecated path — modern
      // composer uses ``RichTextEditor`` instead.
      placeholder = "Su cosa lavoriamo?",
      ...rest
    },
    forwardedRef
  ) => {
    const innerRef = React.useRef<HTMLTextAreaElement | null>(null);
    const isComposingRef = React.useRef(false);

    // Merge forwarded ref + inner ref so we can read scrollHeight imperatively
    // without losing the parent ref hookup.
    React.useImperativeHandle<HTMLTextAreaElement | null, HTMLTextAreaElement | null>(
      forwardedRef,
      () => innerRef.current,
      []
    );

    const resize = React.useCallback(() => {
      const el = innerRef.current;
      if (!el) return;
      // Reset height so scrollHeight reflects content (not previous expansion)
      el.style.height = "auto";
      const next = Math.min(Math.max(el.scrollHeight, minHeight), maxHeight);
      el.style.height = `${next}px`;
      el.style.overflowY = el.scrollHeight > maxHeight ? "auto" : "hidden";
    }, [minHeight, maxHeight]);

    // Initial sync + react to controlled `value` changes. We read `value`
    // explicitly so Biome's useExhaustiveDependencies tracks the trigger;
    // its only role here is to re-run the effect when the controlled prop
    // changes — `resize()` itself reads `innerRef.current.value` natively.
    React.useLayoutEffect(() => {
      void value;
      resize();
    }, [resize, value]);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange?.(e);
      // Uncontrolled path — still resize. For controlled this is redundant
      // (layoutEffect above handles it) but cheap.
      resize();
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      onKeyDown?.(e);
      if (e.defaultPrevented) return;

      // IME composition (CJK, accented Italian é, etc.) — never submit mid-compose.
      if (isComposingRef.current || e.nativeEvent.isComposing) return;

      const isEnter = e.key === "Enter";
      if (!isEnter) return;

      const isCmdEnter = e.metaKey || e.ctrlKey;
      const isPlainEnter = !e.shiftKey && !e.metaKey && !e.ctrlKey && !e.altKey;

      if (isCmdEnter || isPlainEnter) {
        e.preventDefault();
        if (onSubmit) {
          onSubmit();
        } else {
          // Fall back to native form submission — Root is the <form>.
          innerRef.current?.form?.requestSubmit();
        }
      }
      // Shift+Enter → default newline (no-op).
    };

    return (
      <textarea
        ref={innerRef}
        rows={1}
        className={cn(
          // Bleed into the card padding rather than re-add it (matches v18 .composer-input)
          "block w-full resize-none border-0 bg-transparent outline-none",
          "px-3.5 py-2 text-[13px] text-ink-1 leading-[1.55] placeholder:text-ink-3",
          // Hidden custom scrollbar — only appears when content exceeds maxHeight
          "scrollbar-thin",
          className
        )}
        placeholder={placeholder}
        value={value}
        defaultValue={defaultValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onCompositionStart={() => {
          isComposingRef.current = true;
        }}
        onCompositionEnd={() => {
          isComposingRef.current = false;
        }}
        style={{ minHeight, maxHeight }}
        {...rest}
      />
    );
  }
);
ComposerTextarea.displayName = "Composer.Textarea";
